import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Globe2, FileCheck2, Star, Plane, BadgeCheck, HeartHandshake, ListChecks, Quote, Megaphone } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { AppointmentHero } from "@/components/AppointmentHero";
import { Canada2027Announcement } from "@/components/Canada2027Announcement";
import { WteLiveChannel } from "@/components/WteLiveChannel";
import { EligibilitySection } from "@/components/EligibilitySection";
import { ReviewsSection } from "@/components/ReviewsSection";
import { AccompanimentSection } from "@/components/AccompanimentSection";
import { TrustSection } from "@/components/TrustSection";
import { GoogleReviewsSection } from "@/components/GoogleReviewsSection";
import { FlightTrail } from "@/components/FlightTrail";
import { FounderVisionSection } from "@/components/FounderVisionSection";
import { PartnersSection } from "@/components/PartnersSection";
import { useLang } from "@/context/LanguageContext";

const testimonials = [];

export default function Home() {
  const { t } = useLang();

  const steps = [
    { icon: Globe2, title: t("home.step1"), text: t("home.step1t") },
    { icon: FileCheck2, title: t("home.step2"), text: t("home.step2t") },
    { icon: Plane, title: t("home.step3"), text: t("home.step3t") },
    { icon: Star, title: t("home.step4"), text: t("home.step4t") },
  ];
  const advantages = [
    { icon: BadgeCheck, title: t("home.adv1"), text: t("home.adv1t") },
    { icon: HeartHandshake, title: t("home.adv2"), text: t("home.adv2t") },
    { icon: ShieldCheck, title: t("home.adv3"), text: t("home.adv3t") },
    { icon: ListChecks, title: t("home.adv4"), text: t("home.adv4t") },
  ];

  return (
    <div data-testid="home-page">
      <Seo
        title="Work & Travel Expert — Visa & Immigration Australie et Canada"
        description="Accompagnement premium francophone pour vos visas et votre immigration vers l'Australie et le Canada. Évaluation d'éligibilité, préparation de dossier, PVT, permis de travail et suivi en ligne."
        keywords="visa australie, visa canada, immigration australie, immigration canada, PVT, working holiday visa, permis de travail, work travel expert"
        path="/"
      />
      {/* HERO — PRISE DE RENDEZ-VOUS GRATUIT */}
      <AppointmentHero />

      {/* ANNONCE ANIMÉE — CANADA 2027 */}
      <Canada2027Announcement />

      {/* WTE LIVE — CHAÎNE D'INFORMATION */}
      <WteLiveChannel />

      {/* ANNONCE — CLIENTS FRANCOPHONES / PLATEFORME UNIFIÉE */}
      <section data-testid="francophone-announcement" className="bg-[#F8F9FA] border-y border-gold/30">
        <Section className="py-14 sm:py-16">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl bg-navy text-white p-8 sm:p-12">
              <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
              <div className="relative z-10">
                <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold">
                  <Megaphone size={14} /> {t("access.badge")}
                </span>
                <h2 className="mt-5 font-heading text-3xl sm:text-4xl text-white leading-tight">{t("access.title")}</h2>
                <div className="gold-line mt-5" />
                <p className="mt-6 text-white/80 max-w-2xl leading-relaxed">{t("access.intro")}</p>
                <div className="mt-5 flex flex-wrap gap-3" data-testid="francophone-countries">
                  {[
                    { flag: "🇫🇷", label: t("access.france") },
                    { flag: "🇧🇪", label: t("access.belgium") },
                    { flag: "🇨🇭", label: t("access.switzerland") },
                    { flag: "🇱🇺", label: t("access.luxembourg") },
                  ].map((c) => (
                    <span key={c.label} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-4 py-2 text-sm text-white">
                      <span className="text-lg leading-none">{c.flag}</span> {c.label}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-white/70 max-w-2xl leading-relaxed">{t("access.others")}</p>
                <p className="mt-4 text-gold font-medium max-w-2xl leading-relaxed">{t("access.unified")}</p>
                <Link to="/inscription" data-testid="francophone-cta-btn" className="group inline-flex items-center gap-2 mt-8 bg-gold hover:bg-gold-hover text-white px-8 py-3.5 font-medium transition-colors">
                  {t("access.cta")} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </Reveal>
        </Section>
      </section>


      {/* PASSEPORTS ÉLIGIBLES & CONDITIONS */}
      <EligibilitySection />

      <div className="bg-white py-6">
        <FlightTrail className="max-w-5xl mx-auto px-5" />
      </div>

      {/* PROCESS */}
      <div className="bg-[#F8F9FA]">
        <Section className="py-24">
          <Reveal className="text-center max-w-2xl mx-auto">
            <Overline>{t("home.processOverline")}</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-medium text-navy">{t("home.processTitle")}</h2>
            <div className="gold-line mx-auto mt-6" />
          </Reveal>
          <FlightTrail className="mt-8 max-w-4xl mx-auto" />
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div className="bg-white border border-border p-8 h-full transition-all duration-300 hover:border-gold hover:-translate-y-1.5 hover:shadow-xl">
                  <div className="h-12 w-12 bg-navy text-gold flex items-center justify-center"><s.icon size={22} /></div>
                  <h3 className="font-heading text-xl text-navy mt-5">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* VOUS ÊTES ACCOMPAGNÉ À CHAQUE ÉTAPE */}
      <AccompanimentSection />

      {/* WHY CHOOSE US */}
      <div className="bg-navy text-white relative overflow-hidden">
        <Section className="py-24 relative z-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <Overline>{t("home.whyOverline")}</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-medium text-white">{t("home.whyTitle")}</h2>
            <div className="gold-line mx-auto mt-6" />
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.12}>
                <div className="border border-white/10 bg-white/[0.03] p-8 h-full transition-all duration-300 hover:border-gold hover:bg-white/[0.06] hover:-translate-y-1.5">
                  <div className="h-12 w-12 border border-gold text-gold flex items-center justify-center"><a.icon size={22} /></div>
                  <h3 className="font-heading text-xl text-white mt-5">{a.title}</h3>
                  <p className="text-sm text-white/70 mt-2 leading-relaxed">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* UNE VISION NÉE DU VOYAGE */}
      <FounderVisionSection />

      {/* FLORIDA — SOCIÉTÉ AMÉRICAINE (bannière pleine largeur) */}
      <section data-testid="florida-section" className="relative w-full overflow-hidden">
        {/* IMAGE PLEINE LARGEUR */}
        <img src={IMAGES.florida} alt="Skyline de Miami, Floride, États-Unis" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        {/* FILTRE SOMBRE POUR LISIBILITÉ */}
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/55 to-navy/25" />

        {/* BADGE PAR-DESSUS L'IMAGE */}
        <div data-testid="florida-badge" className="absolute top-6 left-5 sm:left-8 z-20 flex items-center gap-2 bg-navy/70 backdrop-blur-sm border border-white/20 px-4 py-2">
          <span className="text-lg leading-none">🇺🇸</span>
          <span className="text-white text-sm tracking-wide">{t("florida.imgCaption")}</span>
        </div>

        {/* CONTENU */}
        <Section className="relative z-10 py-24 sm:py-28 lg:py-32">
          <Reveal>
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold">
                <span className="text-sm leading-none">🇺🇸</span> {t("florida.badge")}
              </span>
              <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
                <span className="mr-2">🇺🇸</span>{t("florida.title")}
              </h2>
              <div className="gold-line mt-6" />
              <div className="mt-6 space-y-4 text-white/85 leading-relaxed">
                <p>{t("florida.p1")}</p>
                <p>{t("florida.p2")}</p>
              </div>
            </div>
          </Reveal>
        </Section>
      </section>

      {/* CONFIANCE & TRANSPARENCE — CERTIFICAT OFFICIEL */}
      <TrustSection theme="light" />

      {/* AVIS GOOGLE */}
      <GoogleReviewsSection theme="light" />

      {/* PARTENAIRES & SERVICES RECOMMANDÉS */}
      <PartnersSection />

      {/* TESTIMONIALS */}
      <Section className="py-24">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Overline>{t("home.testiOverline")}</Overline>
          <h2 className="mt-4 text-3xl sm:text-4xl font-medium text-navy">{t("home.testiTitle")}</h2>
          <div className="gold-line mx-auto mt-6" />
          <p className="mt-5 text-muted-foreground">{t("home.testiText")}</p>
        </Reveal>
        {testimonials.length ? (
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.map((tm, i) => (
              <Reveal key={tm.name} delay={i * 0.1}>
                <div className="bg-white border border-border p-8 h-full flex flex-col">
                  <Quote size={32} className="text-gold" />
                  <div className="flex gap-1 mt-4">{[...Array(5)].map((_, s) => <Star key={s} size={16} className="fill-gold text-gold" />)}</div>
                  <p className="text-slate-700 mt-4 leading-relaxed flex-1 italic">"{tm.text}"</p>
                  <div className="mt-6"><p className="font-heading text-lg text-navy">{tm.name}</p><p className="text-xs text-gold uppercase tracking-widest">{tm.destination}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal delay={0.1}>
            <div data-testid="avis-empty-state" className="mt-14 border border-dashed border-gold/50 bg-[#F8F9FA] py-16 px-8 text-center">
              <Quote size={40} className="mx-auto text-gold" />
              <p className="mt-5 font-heading text-2xl text-navy">{t("home.testiEmpty")}</p>
            </div>
          </Reveal>
        )}
      </Section>

      {/* GUIDES & RESSOURCES — maillage interne SEO */}
      <div className="bg-[#F8F9FA]">
        <Section className="py-24">
          <Reveal className="text-center max-w-2xl mx-auto">
            <Overline>Guides & ressources</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-medium text-navy">Tout savoir sur les visas Australie & Canada</h2>
            <div className="gold-line mx-auto mt-6" />
            <p className="mt-5 text-muted-foreground">Des guides clairs et à jour pour préparer votre projet, choisir le bon visa et réussir votre installation.</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { to: "/visa-australie", tag: "Australie 🇦🇺", title: "Visa Australie : types, conditions et démarches" },
              { to: "/travailler-en-australie", tag: "Australie 🇦🇺", title: "Travailler en Australie : emploi et salaires" },
              { to: "/visa-canada", tag: "Canada 🇨🇦", title: "Visa Canada : PVT, permis de travail et Entrée Express" },
              { to: "/immigration-canada", tag: "Canada 🇨🇦", title: "Immigration Canada : programmes et étapes clés" },
            ].map((g, i) => (
              <Reveal key={g.to} delay={i * 0.08}>
                <Link to={g.to} data-testid={`home-guide-${g.to.slice(1)}`} className="group flex flex-col h-full bg-white border border-border p-6 hover:border-gold transition-colors">
                  <span className="text-xs uppercase tracking-widest font-semibold text-gold">{g.tag}</span>
                  <h3 className="font-heading text-lg text-navy mt-3 leading-snug flex-1 group-hover:text-gold transition-colors">{g.title}</h3>
                  <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-navy group-hover:gap-2.5 transition-all">Lire le guide <ArrowRight size={15} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/blog" data-testid="home-blog-link" className="inline-flex items-center gap-2 text-gold font-medium hover:gap-3 transition-all">
              Voir tous nos guides sur le blog <ArrowRight size={16} />
            </Link>
          </div>
        </Section>
      </div>

      {/* AVIS CLIENTS */}
      <ReviewsSection />

      {/* CTA */}
      <div className="relative overflow-hidden">
        <img src={IMAGES.canada} alt="Canada" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/85" />
        <Section className="relative z-10 py-24 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-5xl font-medium text-white max-w-3xl mx-auto leading-tight">{t("home.ctaTitle")}</h2>
            <p className="text-white/75 mt-5 max-w-xl mx-auto">{t("home.ctaText")}</p>
            <Link to="/nos-packs" data-testid="cta-reserve-btn" className="inline-flex items-center gap-2 mt-9 bg-gold hover:bg-gold-hover text-white px-9 py-4 font-medium transition-colors">
              {t("home.ctaBtn")} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </Section>
      </div>
    </div>
  );
}
